import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RequireWrite, TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { createLp, type LpType } from "@/lib/api/lps";

const LP_TYPES: LpType[] = [
  "pension",
  "sovereign",
  "endowment",
  "foundation",
  "insurance",
  "fof",
  "family_office",
  "hnw",
  "corporate",
  "other",
];

function LpsNewInner() {
  const tenant = useTenant();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [legalName, setLegalName] = useState("");
  const [shortName, setShortName] = useState("");
  const [lpType, setLpType] = useState<LpType | "">("");
  const [domicile, setDomicile] = useState("");
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: createLp,
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["lps", tenant.id] });
      await navigate({ to: "/t/$slug/lps", params: { slug: tenant.slug } });
    },
    onError: (err: Error) => setError(err.message),
  });

  function onSubmit(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    mutation.mutate({
      tenant_id: tenant.id,
      legal_name: legalName,
      short_name: shortName || null,
      lp_type: lpType || null,
      domicile: domicile || null,
    });
  }

  return (
    <>
      <header className="space-y-1">
        <Link
          to="/t/$slug/lps"
          params={{ slug: tenant.slug }}
          className="text-xs text-neutral-500 hover:text-neutral-300"
        >
          ← Back to LPs
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">New LP</h1>
      </header>

      <form onSubmit={onSubmit} className="max-w-2xl space-y-4">
        <Field label="Legal name" required>
          <input
            required
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
            className="input"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Short name">
            <input
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Domicile">
            <input
              value={domicile}
              onChange={(e) => setDomicile(e.target.value)}
              className="input"
              placeholder="USA, UK, Singapore, …"
            />
          </Field>
        </div>

        <Field label="LP type">
          <select
            value={lpType}
            onChange={(e) => setLpType(e.target.value as LpType | "")}
            className="input"
          >
            <option value="">—</option>
            {LP_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>

        {error ? (
          <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
            {error}
          </div>
        ) : null}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={mutation.isPending}
            className="rounded bg-neutral-100 px-4 py-2 text-sm font-medium text-neutral-950 transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? "Creating…" : "Create LP"}
          </button>
          <Link
            to="/t/$slug/lps"
            params={{ slug: tenant.slug }}
            className="rounded border border-neutral-800 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-900"
          >
            Cancel
          </Link>
        </div>
      </form>
    </>
  );
}

interface FieldProps {
  label: string;
  required?: boolean;
  children: ReactNode;
}

function Field({ label, required, children }: FieldProps) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase tracking-wide text-neutral-400">
        {label}
        {required ? <span className="ml-0.5 text-red-400">*</span> : null}
      </span>
      {children}
    </label>
  );
}

export function LpsNewPage() {
  return (
    <TenantLayout>
      <RequireWrite>
        <LpsNewInner />
      </RequireWrite>
    </TenantLayout>
  );
}
